import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { PartHistoryDataType } from '../../../@types/part.type';
import { ServiceObject } from '../../../@types/service.type';
import { getServiceById } from '../../../api/services';
import { ServiceTypeCode } from '../../../configs/enums';
import { GetServiceKey } from '../../../configs/services';
import { useGetPartHistoryByPartId } from '../../../queries/part-query';

const SERVICE_STALE_TIME_MS = 60 * 1000;
const SERVICE_GC_TIME_MS = 5 * 60 * 1000;

/** Unique serviceIds from part history, ordered most-recent-first by entry createdAt. */
const orderedServiceIds = (history: PartHistoryDataType[]): number[] => {
  const seen = new Map<number, string>();
  for (const entry of history) {
    if (!entry.serviceId) continue;
    const existing = seen.get(entry.serviceId);
    if (!existing || (entry.createdAt ?? '') > existing) {
      seen.set(entry.serviceId, entry.createdAt ?? '');
    }
  }
  return Array.from(seen.entries())
    .sort((a, b) => b[1].localeCompare(a[1]))
    .map(([id]) => id);
};

export interface OpenRepairJobResult {
  openRepairJob: ServiceObject | null;
  isLoading: boolean;
}

/**
 * Resolves the most-recently-touched open repair job linked to a part by walking
 * the part's history for serviceIds and inspecting each service. Shares the
 * React Query cache with LinkedJobsPanel and useGetPartHistoryByPartId.
 */
export const useOpenRepairJob = (partId: number): OpenRepairJobResult => {
  const partHistoryQuery = useGetPartHistoryByPartId(partId);
  const ids = useMemo(
    () => orderedServiceIds(partHistoryQuery.data ?? []),
    [partHistoryQuery.data]
  );

  const serviceQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: [GetServiceKey, id],
      queryFn: ({ signal }: { signal?: AbortSignal }) => getServiceById(id, signal),
      staleTime: SERVICE_STALE_TIME_MS,
      gcTime: SERVICE_GC_TIME_MS,
      enabled: !!id,
    })),
  });

  const isLoading =
    partHistoryQuery.isLoading || (ids.length > 0 && serviceQueries.some((q) => q.isLoading));

  const openRepairJob = useMemo<ServiceObject | null>(() => {
    for (const q of serviceQueries) {
      const data = q.data;
      if (
        data?.serviceTypeCode === ServiceTypeCode.Repair &&
        data?.serviceStatus?.isClosed === false
      ) {
        return data;
      }
    }
    return null;
  }, [serviceQueries]);

  return { openRepairJob, isLoading };
};
